import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, debounceTime, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';

import { ROUND_COORDINATE } from '@common/utils/consts/constants';
import {
  clearAddressCache,
  loadAddressFailure,
  loadAddressForCoordinates,
  loadAddressSuccess,
  overwriteAddressMap,
  triggerAddressCleanup,
  triggerAddressReload,
} from '@store/actions/address.actions';
import { selectMonitoringEntities } from '@store/monitoring/monitoring.selectors';
import { selectAddressMap, selectUsedCoordinateKeys } from '@store/selectors/address.selectors';

import { MapService } from '../../app/portal/components/map/services/map.service';
import { MonitoringItem } from '../../app/portal/pages/monitoring-page/monitoring.types';

export const normalizeCoordinateKey = (key: string): string => {
  const [lng, lat] = key.split('_');
  return `${Number(parseFloat(lng).toFixed(ROUND_COORDINATE))}_${Number(parseFloat(lat).toFixed(ROUND_COORDINATE))}`;
};

@Injectable()
export class AddressEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly mapService = inject(MapService);

  private requestedKeys = new Set<string>();

  loadAddressForCoordinates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadAddressForCoordinates),
      withLatestFrom(this.store.select(selectAddressMap)),
      mergeMap(([{ keys }, cache]) => {
        const missing = keys
          .map(normalizeCoordinateKey)
          .filter((k) => !(k in cache) && !this.requestedKeys.has(k));

        if (!missing.length) return of({ type: '[Address] No load needed' });
        missing.forEach((k) => this.requestedKeys.add(k));

        const requests = this.chunkArray(missing, 1000).map(
          (chunk) =>
            this.mapService.getAddress(
              chunk.map((k) => {
                const [lng, lat] = k.split('_');
                return { lat, lng };
              }),
            ) as Observable<Record<string, { address: string }>>,
        );

        return forkJoin(requests).pipe(
          map((responses) =>
            responses.reduce(
              (acc, res) => {
                for (const raw in res) {
                  const key = normalizeCoordinateKey(raw);
                  acc[key] = res[raw].address;
                  this.requestedKeys.delete(key);
                }
                return acc;
              },
              {} as Record<string, string>,
            ),
          ),
          map((map) => loadAddressSuccess({ map })),
          catchError((err) => {
            missing.forEach((k) => this.requestedKeys.delete(k));
            return of(loadAddressFailure({ error: err.message || 'Ошибка геокодинга' }));
          }),
        );
      }),
    ),
  );

  autoLoadMissingAddresses$ = createEffect(() =>
    combineLatest([
      this.store.select(selectMonitoringEntities),
      this.store.select(selectAddressMap),
    ]).pipe(
      debounceTime(200),
      map(([entities, cache]) => {
        const keys = Object.values(entities)
          .filter((e): e is MonitoringItem => !!e && !!e.coordinateKey)
          .map((e) => normalizeCoordinateKey(e.coordinateKey!));

        const missing = keys.filter((k) => !(k in cache) && !this.requestedKeys.has(k));
        return missing.length
          ? loadAddressForCoordinates({ keys: missing })
          : { type: '[Address] No load needed' };
      }),
    ),
  );

  cleanupUnusedAddresses$ = createEffect(() =>
    combineLatest([
      this.store.select(selectAddressMap),
      this.store.select(selectUsedCoordinateKeys),
    ]).pipe(
      take(1),
      map(([addressMap, usedKeys]) => {
        const cleaned: Record<string, string> = {};
        for (const key in addressMap) {
          if (usedKeys.has(key)) {
            cleaned[key] = addressMap[key];
          }
        }
        return loadAddressSuccess({ map: cleaned });
      }),
    ),
  );

  triggerCleanup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerAddressCleanup),
      withLatestFrom(
        this.store.select(selectAddressMap),
        this.store.select(selectUsedCoordinateKeys),
      ),
      map(([_, addressMap, usedKeys]) => {
        const cleaned: Record<string, string> = {};
        for (const key in addressMap) {
          if (usedKeys.has(key)) cleaned[key] = addressMap[key];
        }
        return loadAddressSuccess({ map: cleaned });
      }),
    ),
  );

  triggerReload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerAddressReload),
      withLatestFrom(
        this.store.select(selectMonitoringEntities),
        this.store.select(selectAddressMap),
      ),
      map(([_, entities, addressMap]) => {
        const allKeys = Object.values(entities)
          .map((e) => e?.coordinateKey)
          .filter((k): k is string => !!k);
        const missing = allKeys.filter((key) => !(key in addressMap));

        return missing.length
          ? loadAddressForCoordinates({ keys: missing })
          : { type: '[Address] Nothing to reload' };
      }),
    ),
  );

  clearAddressCache$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clearAddressCache),
      map(() => overwriteAddressMap({ map: {} })),
    ),
  );

  private chunkArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  }
}
